import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Routes, Route, useNavigate, useLocation, Link } from 'react-router-dom';
import { Plus, Search, ChevronRight, Star, Diamond, User as UserIcon } from 'lucide-react';
import { useAuth } from '../../contexts/auth/hooks';
import { supabase } from '../../lib/supabase';
import { Database } from '../../lib/database.types';
import { CustomerCard } from './components/CustomerCard';
import { CustomerFormModal } from './components/CustomerFormModal';
import CustomerDetails from './CustomerDetails';
import CustomerLoyalty from './CustomerLoyalty';
import CustomerDocuments from './CustomerDocuments';
import { ImportCustomersModal } from './components/ImportCustomersModal';
import { ExportCustomersModal } from './components/ExportCustomersModal';
import { useSubscription } from '../../hooks/useSubscription';
import { useLoyaltySettings } from '../../hooks/useLoyaltySettings';
import toast from 'react-hot-toast';
import { getFriendlyErrorMessage } from '../../utils/errorMessages';

type Customer = Database['public']['Tables']['customers']['Row'];

function Customers() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isFeatureAvailable } = useSubscription();
  const { isLoyaltyEnabled, loyaltySettings, refreshLoyaltySettings } = useLoyaltySettings();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [loyaltyFilter, setLoyaltyFilter] = useState<string>('all');
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [importModal, setImportModal] = useState(false);
  const [exportModal, setExportModal] = useState(false);
  const [computedCustomers, setComputedCustomers] = useState<Customer[]>([]);

  const loyaltyEnabled = isFeatureAvailable('loyalty_program') && isLoyaltyEnabled;

  useEffect(() => {
    loadBusinessData();
  }, []);

  useEffect(() => {
    if (location.state?.openNewCustomerModal) {
      setEditingCustomer(null);
      setShowForm(true);
    }
  }, [location.state]);

  useEffect(() => {
    const handleRefresh = () => {
      if (businessId) loadCustomers(businessId);
    };

    window.addEventListener('refresh-customers', handleRefresh);
    return () => window.removeEventListener('refresh-customers', handleRefresh);
  }, [businessId]);

  useEffect(() => {
    if (businessId && loyaltySettings?.levels) {
      loadCustomers(businessId);
    }
  }, [loyaltySettings, businessId]);

  useEffect(() => {
    if (customers.length && loyaltySettings?.levels) {
      const updated = customers.map(customer => {
        const loyaltyLevel = calculateLoyaltyLevel(customer.diamonds, loyaltySettings.levels);
        const nextLevel = getNextLevel(customer.diamonds, loyaltySettings.levels, loyaltyLevel);
        return {
          ...customer,
          loyalty_level: loyaltyLevel,
          nextLevel
        };
      });
      setComputedCustomers(updated);
    } else {
      setComputedCustomers(customers);
    }
  }, [customers, loyaltySettings]);

  const loadBusinessData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('משתמש לא מחובר');

      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('business_id')
        .eq('id', user.id)
        .single();

      if (userError) throw userError;
      if (!userData?.business_id) throw new Error('לא נמצא עסק מקושר');

      setBusinessId(userData.business_id);
      loadCustomers(userData.business_id);
    } catch (error: any) {

      toast.error(error.message || 'שגיאה בטעינת נתוני העסק');
      setLoading(false);
    }
  };

  const calculateLoyaltyLevel = (
    diamonds: number,
    levels: Record<string, { diamonds_required: number }>
  ) => {
    const sorted = Object.entries(levels)
      .sort(([, a], [, b]) => a.diamonds_required - b.diamonds_required);
    let currentLevel = sorted[0][0];
    for (let i = 0; i < sorted.length; i++) {
      const [level, config] = sorted[i];
      if (diamonds < config.diamonds_required) break;
      currentLevel = level;
    }
    return currentLevel;
  };

  const getNextLevel = (diamonds: number, levels: Record<string, { diamonds_required: number }>, currentLevel: string) => {
    const sorted = Object.entries(levels)
      .sort(([, a], [, b]) => a.diamonds_required - b.diamonds_required)
      .map(([key]) => key);
    const currentIndex = sorted.indexOf(currentLevel);
    const nextIndex = currentIndex + 1;
    if (nextIndex >= sorted.length) return null;
    const nextKey = sorted[nextIndex];
    const required = levels[nextKey].diamonds_required;
    return {
      key: nextKey,
      requiredDiamonds: required,
      remaining: Math.max(required - diamonds, 0)
    };
  };

  const loadCustomers = async (id: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('business_id', id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setCustomers(data || []);
    } catch (error: any) {

      toast.error('שגיאה בטעינת הלקוחות');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (data: Partial<Customer>) => {
    if (!businessId) return;

    try {
      if (editingCustomer) {
        const { error } = await supabase
          .from('customers')
          .update(data)
          .eq('id', editingCustomer.id);

        if (error) throw error;
        toast.success('הלקוח עודכן בהצלחה');
      } else {
        let defaultLevel = 'silver';
        if (loyaltySettings?.levels) {
          const sorted = Object.entries(loyaltySettings.levels)
            .sort(([, a], [, b]) => a.diamonds_required - b.diamonds_required);
          defaultLevel = sorted[0][0];
        }
        const { error } = await supabase
          .from('customers')
          .insert([{
            ...data,
            business_id: businessId,
            points: 0,
            diamonds: 0,
            loyalty_level: defaultLevel,
            loyalty_stats: {
              total_visits: 0,
              consecutive_visits: 0,
              last_visit: null,
              referrals: 0,
              total_spent: 0,
              achievements: []
            }
          }]);

        if (error) throw error;
        toast.success('הלקוח נוסף בהצלחה');
      }

      setShowForm(false);
      setEditingCustomer(null);
      loadCustomers(businessId);
    } catch (error: any) {

      const friendly = getFriendlyErrorMessage(error.message);
      toast.error(friendly);
    }
  };

  const handleImportComplete = async () => {
    if (businessId) {
      await loadCustomers(businessId);
    }
    toast.success('הייבוא הסתיים בהצלחה!');
    setImportModal(false);
  };

  const filteredCustomers = computedCustomers.filter(customer => {
    const matchesSearch =
      customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.phone.includes(searchTerm) ||
      (customer.email && customer.email.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesLoyalty = loyaltyFilter === 'all' || customer.loyalty_level === loyaltyFilter;

    return matchesSearch && matchesLoyalty;
  });

  const getLoyaltyColor = (level: string) => {
    switch (level) {
      case 'vip': return 'text-purple-600';
      case 'diamond': return 'text-blue-600';
      case 'gold': return 'text-amber-600';
      case 'silver': return 'text-gray-600';
      default: return 'text-gray-500';
    }
  };

  const getLoyaltyIcon = (level: string) => {
    switch (level) {
      case 'vip': return '👑';
      case 'diamond': return '💎';
      case 'gold': return '🥇';
      case 'silver': return '🥈';
      default: return '⭐';
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Routes>
      <Route path="/" element={
        <div className="min-h-screen bg-gray-50">
          {/* iOS-style Header */}
          <div className="bg-white border-b border-gray-200">
            <div className="px-4 pt-12 pb-4">
              <h1 className="text-4xl font-bold tracking-tight text-gray-900">לקוחות</h1>
              <p className="text-sm text-gray-500 mt-1">{filteredCustomers.length} לקוחות</p>
            </div>

            {/* Search Bar */}
            <div className="px-4 pb-4">
              <div className="relative">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="חיפוש"
                  className="w-full pr-10 pl-4 py-2.5 bg-gray-100 border-0 rounded-xl focus:ring-0 focus:bg-gray-200 transition-colors text-base"
                />
              </div>
            </div>

            {/* Filter Pills */}
            {loyaltyEnabled && (
              <div className="px-4 pb-3 overflow-x-auto">
                <div className="flex gap-2">
                  {['all', 'vip', 'diamond', 'gold', 'silver'].map((filter) => (
                    <button
                      key={filter}
                      onClick={() => setLoyaltyFilter(filter)}
                      className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                        loyaltyFilter === filter
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {filter === 'all' ? 'הכל' : filter === 'vip' ? 'VIP' : filter === 'diamond' ? 'יהלום' : filter === 'gold' ? 'זהב' : 'כסף'}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Customer List - iOS Style */}
          <div className="pb-24">
            {loading ? (
              <div className="flex justify-center py-20">
                <div className="w-8 h-8 border-3 border-blue-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredCustomers.length > 0 ? (
              <div className="bg-white mt-4 mx-4 rounded-2xl overflow-hidden shadow-sm">
                {filteredCustomers.map((customer, index) => (
                  <motion.div
                    key={customer.id}
                    whileTap={{ scale: 0.98, backgroundColor: 'rgba(0,0,0,0.02)' }}
                    onClick={() => navigate(`/customers/${customer.id}`)}
                    className={`flex items-center gap-4 p-4 cursor-pointer transition-colors ${
                      index !== filteredCustomers.length - 1 ? 'border-b border-gray-100' : ''
                    }`}
                  >
                    {/* Avatar */}
                    <div className="relative flex-shrink-0">
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                        <span className="text-white text-lg font-semibold">
                          {getInitials(customer.name)}
                        </span>
                      </div>
                      {loyaltyEnabled && (
                        <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-sm">
                          <span className="text-xs">{getLoyaltyIcon(customer.loyalty_level)}</span>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-base text-gray-900 truncate">
                          {customer.name}
                        </h3>
                        <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      </div>
                      <p className="text-sm text-gray-500 truncate">{customer.phone}</p>
                      {loyaltyEnabled && (
                        <div className="flex items-center gap-3 mt-1">
                          <div className="flex items-center gap-1">
                            <Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400" />
                            <span className="text-xs text-gray-600 font-medium">{customer.points}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Diamond className="h-3.5 w-3.5 text-blue-400 fill-blue-400" />
                            <span className="text-xs text-gray-600 font-medium">{customer.diamonds}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <UserIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-400 mb-6">לא נמצאו לקוחות</p>
                <button
                  onClick={() => {
                    setEditingCustomer(null);
                    setShowForm(true);
                  }}
                  className="text-blue-500 hover:text-blue-600 font-medium"
                >
                  הוסף לקוח חדש
                </button>
              </div>
            )}
          </div>

          {/* Floating Action Button */}
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => {
              setEditingCustomer(null);
              setShowForm(true);
            }}
            className="fixed bottom-24 left-6 w-16 h-16 bg-blue-500 text-white rounded-full shadow-2xl flex items-center justify-center hover:bg-blue-600 active:bg-blue-700 transition-colors z-40"
          >
            <Plus className="h-7 w-7" />
          </motion.button>

          {/* Modals */}
          <CustomerFormModal
            isOpen={showForm}
            customer={editingCustomer || undefined}
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowForm(false);
              setEditingCustomer(null);
            }}
          />

          <AnimatePresence>
            {importModal && businessId && (
              <ImportCustomersModal
                onClose={() => setImportModal(false)}
                onImportComplete={handleImportComplete}
                businessId={businessId}
              />
            )}

            {exportModal && (
              <ExportCustomersModal
                onClose={() => setExportModal(false)}
                customers={customers}
              />
            )}
          </AnimatePresence>
        </div>
      } />
      <Route path=":id" element={<CustomerDetails />} />
      <Route path=":id/loyalty" element={<CustomerLoyalty />} />
      <Route path=":id/documents" element={<CustomerDocuments />} />
    </Routes>
  );
}

export default Customers;
